import { useQuery } from '@tanstack/react-query'
import { t } from 'i18next'

import { ButtonsCancelSave } from '~/components/Buttons'
import Line from '~/components/Line'
import Panel from '~/components/Panel'
import RangeSlider from '~/components/RangeSlider'
import Tag from '~/components/Tag'
import Tooltip from '~/components/Tooltip'
import { Checkbox, Input, Label } from '~/components/unform'
import Form from '~/components/unform/Form'
import { isAdvertiser } from '~/helpers'
import { DailyBudget } from '~/modules/retailMedia/components/Budget/DailyBudget'
import DurationDate from '~/modules/retailMedia/components/DurationDate'
import EditButton from '~/modules/retailMedia/components/EditButton'
import { GOAL_AD_COST } from '~/modules/retailMedia/constants/objectiveBased'
import {
  getCampaignPermissionsService,
  getCampaignPermissionsServiceKey
} from '~/modules/retailMedia/services/configuration/campaign-permissions'

import { useCampaignHeader } from './useCampaignHeader'

import * as S from './styles'

export const ObjectiveBasedCampaignHeader = ({ data }: CampaignHeaderProps) => {
  const publisherId =
    data.publisher_id || data.publisher?.id || data.raw?.publisher_id
  const { data: campaignPermissions } = useQuery({
    enabled: !!publisherId,
    queryKey: [getCampaignPermissionsServiceKey, { publisher_id: publisherId }],
    queryFn: async () => {
      const response = await getCampaignPermissionsService({
        publisher_id: publisherId
      })

      return response.data
    }
  })
  const {
    isEditMode,
    setIsEditMode,
    goal,
    canEdit,
    initialData,
    handleSubmit,
    isSavingLoading,
    formRef
  } = useCampaignHeader(data)

  return (
    <Panel
      title={t('rm:generalInformation')}
      template={isEditMode ? 'borderWarning' : 'default'}
      headerSideContent={
        isAdvertiser() &&
        canEdit && (
          <EditButton
            onClick={() => setIsEditMode((e: boolean) => !e)}
            isActive={isEditMode}
          />
        )
      }
    >
      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={initialData}
        className="form-default"
      >
        <S.Container>
          <div>
            <div className="mb-1 d-flex align-items-center">
              <S.Title>{t('rm:objectiveBased.strategy')}</S.Title>
              <Tooltip text={t('rm:objectiveBased.strategy.tooltip')} />
            </div>
            <Tag>
              {goal && Object.hasOwn(GOAL_AD_COST, goal)
                ? t(`rm:objectiveBased.strategy.${goal}`)
                : t('rm:objectiveBased.campaign')}
            </Tag>
          </div>

          <S.SectionWrapper>
            <S.RangeWrapper>
              <div className="d-flex align-items-center">
                <S.Title>{t('rm:objectiveBased.acos')}</S.Title>
                <Tooltip text={t('rm:objectiveBased.acos.tooltip')} />
              </div>
              <RangeSlider
                isRangeValue
                min={goal ? (GOAL_AD_COST[goal]?.min ?? 0) : 0}
                max={goal ? (GOAL_AD_COST[goal]?.max ?? 0) : 0}
              />
            </S.RangeWrapper>
          </S.SectionWrapper>

          <S.SectionWrapper>
            <DailyBudget
              data={data}
              inputName="budget.daily"
              isEditMode={isEditMode}
            />
          </S.SectionWrapper>
        </S.Container>

        <Line margin="1rem" />

        <S.Container>
          {isEditMode ? (
            <div>
              <Label className="text-primary">{t('rm:campaignName')}</Label>
              <Input name="settings.name" required />
            </div>
          ) : (
            <div>
              <S.Title>{t('rm:campaignName')}</S.Title>
              <p className="m-0">{data.title}</p>
            </div>
          )}

          <DurationDate
            data={data}
            isEditMode={isEditMode}
            inputNames={{
              startDate: 'settings.start_date',
              endDate: 'settings.end_date',
              checkBox: 'settings.is_always_on'
            }}
          />
        </S.Container>

        {campaignPermissions?.allow_age_restricted_campaign && (
          <S.Container>
            {isEditMode ? (
              <Panel
                title={t('rm:AdvancedSettings.title')}
                isCollapsible
                initialCollapsibleState={false}
              >
                <Checkbox
                  name="settings.age_restricted"
                  label={t('rm:campaign.settings.ageRestricted.label')}
                />
              </Panel>
            ) : (
              <div>
                <S.Title>
                  {t('rm:campaign.settings.ageRestricted.title')}
                </S.Title>
                <p className="m-0">
                  {data.settings?.age_restricted
                    ? t('rm:campaign.settings.ageRestricted.value.yes')
                    : t('rm:campaign.settings.ageRestricted.value.no')}
                </p>
              </div>
            )}
          </S.Container>
        )}

        {isEditMode && canEdit && (
          <ButtonsCancelSave
            onCancelClick={() => setIsEditMode(false)}
            isLoading={isSavingLoading}
            showSavingChangesLoading
          />
        )}
      </Form>
    </Panel>
  )
}
